/**
 * Which app each tenant role opens — PURE, no I/O.
 *
 * Obrava has two client surfaces: the App de planta (the operator's tablet,
 * offline-first) and the Backoffice (office management). A role only lets its
 * holder into the apps listed here, and both apps enforce it at the door:
 * planta at sign-in and obrador switch, the Backoffice in its AuthGate. What each
 * role may DO inside an app is still decided by RLS.
 *
 * Roles are combinable, so a MEMBER's apps are the union of their roles' apps:
 * operari + compres works in both. That is how the rule is applied rather than
 * worked around (decisió JM, 2026-09-22): an administrator who also works on the
 * floor carries Operari too, and an operari who needs the office carries Viewer.
 *
 * OBR-25 SUPERSEDES that workaround with two lists: Usuaris (email, open the
 * Backoffice) and Operaris (PIN, open planta), a person who needs both doors having
 * two records. The 2026-09-22 rule is NOT retired: it stays valid for builds <= 28 of
 * planta (which still sign in with email) and for the rows that already exist
 * (cohorts C1–C4, docs/Pla_Implementacio_OBR25_Usuaris_PIN.md §9.1). `canAccessApp`
 * does not change; PIN_ROLES / EMAIL_ROLES only limit what a NEW entry of each list
 * may carry.
 *
 *   - operator            → planta     (executes OTs, weighs, closes lots)
 *   - production_manager  → both       (plans in the office, supervises on the floor)
 *   - quality             → both       (FT/APPCC in the office; corrects pesades on the floor)
 *   - admin · purchasing · sales · viewer → backoffice
 *
 * A member with no roles opens neither.
 */

import { USER_ROLES, type UserRole } from '../enums/index.js';

/**
 * Backoffice first: it is the order of the columns on Usuaris i rols, and every list
 * of apps (the access line, the role options) follows it so the screen reads one way.
 */
export const APP_SURFACES = ['backoffice', 'planta'] as const;
export type AppSurface = (typeof APP_SURFACES)[number];

export const ROLE_APPS: Readonly<Record<UserRole, readonly AppSurface[]>> = {
  admin: ['backoffice'],
  production_manager: ['backoffice', 'planta'],
  operator: ['planta'],
  quality: ['backoffice', 'planta'],
  purchasing: ['backoffice'],
  sales: ['backoffice'],
  viewer: ['backoffice'],
};

/** The apps a set of (combinable) roles gives access to, in APP_SURFACES order. */
export function appsForRoles(roles: readonly UserRole[]): readonly AppSurface[] {
  return APP_SURFACES.filter((app) => roles.some((r) => ROLE_APPS[r]?.includes(app)));
}

/**
 * The roles that open `app`, in USER_ROLES order (Administrador first). The Usuaris i
 * rols screen has one column per app built from this, so a role that opens both apps
 * (Producció, Qualitat) shows in both, and an empty list means no access to that app.
 */
export function rolesForApp(roles: readonly UserRole[], app: AppSurface): readonly UserRole[] {
  return USER_ROLES.filter((r) => roles.includes(r) && ROLE_APPS[r].includes(app));
}

/** True when any of the roles opens `app` — the check both apps run at the door. */
export function canAccessApp(roles: readonly UserRole[], app: AppSurface): boolean {
  return appsForRoles(roles).includes(app);
}

/**
 * The roles a PIN operator may carry: the ones that open planta. Never Administrador —
 * a PIN is a floor credential, and the administration roles live behind an email.
 */
export const PIN_ROLES: readonly UserRole[] = rolesForApp(USER_ROLES, 'planta');

/** The roles a Backoffice user (email) may carry: the ones that open the Backoffice. */
export const EMAIL_ROLES: readonly UserRole[] = rolesForApp(USER_ROLES, 'backoffice');

/**
 * True when the roles open planta and NOT the Backoffice (today: Operari alone). That is
 * the only kind of person "Passa a PIN" can convert without taking their Backoffice
 * access away.
 */
export function isPlantaOnly(roles: readonly UserRole[]): boolean {
  return roles.length > 0 && !canAccessApp(roles, 'backoffice');
}

/** Which credential a person signs in with: a PIN (planta) or an email (Backoffice). */
export type CredentialKind = 'pin' | 'email';

/**
 * Whether `roles` may be assigned to a NEW entry of the list that `kind` names — the
 * rule behind Q7 (decisió JM, 2026-10-12): a PIN carries planta roles only, an email
 * carries Backoffice roles only, and a person with no roles is neither.
 *
 * It is about what can be ASSIGNED, not about what exists: rows created before OBR-25
 * (an email member with Operari, cohorts C1–C4) are not judged by it, and `canAccessApp`
 * is still the only door check.
 */
export function rolesAllowedForCredential(
  kind: CredentialKind,
  roles: readonly UserRole[],
): boolean {
  if (roles.length === 0) return false;
  const allowed = kind === 'pin' ? PIN_ROLES : EMAIL_ROLES;
  return roles.every((r) => allowed.includes(r));
}
